import React, { createContext, useContext, useState, useEffect } from 'react';

const PricingContext = createContext();

// Cache configuration
const CACHE_KEY = 'pricing_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds


export const PricingProvider = ({ children }) => {
  const [pricing, setPricing] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [error, setError] = useState(null);

  // Load cached pricing data
  const loadCachedPricing = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();
        
        // Check if cache is still valid
        if (now - timestamp < CACHE_DURATION) {
          console.log('📦 Using cached pricing data');
          setPricing(data);
          setLastFetchTime(new Date(timestamp));
          return true;
        } else {
          console.log('⏰ Cache expired, will fetch fresh data');
          localStorage.removeItem(CACHE_KEY);
        }
      }
    } catch (error) {
      console.warn('Failed to load cached pricing:', error);
      localStorage.removeItem(CACHE_KEY);
    }
    return false;
  };

  // Save pricing data to cache
  const cachePricing = (data) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      setLastFetchTime(new Date());
      console.log('💾 Pricing data cached successfully');
    } catch (error) {
      console.warn('Failed to cache pricing data:', error);
    }
  };

  // Fetch pricing from API
  const fetchPricing = async (showToast = true) => {
    console.log('🔄 Fetching pricing from API...');
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/stripe/prices`
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.data) {
          console.log('✅ Pricing fetched successfully from API');
          setPricing(data.data);
          cachePricing(data.data);
          setError(null);
          return data.data;
        } else {
          throw new Error('Invalid data structure received from API');
        }
      } else {
        const errorText = await response.text();
        throw new Error(`API returned ${response.status}: ${errorText}`);
      }
    } catch (fetchError) {
      console.error('❌ Failed to fetch pricing:', fetchError.message);
      setError(fetchError.message);
      
      // Keep existing pricing (cached data if available) on error
      if (showToast) {
        // Only show toast if explicitly requested (manual refresh)
        console.log('API unavailable - using cached data if available');
      }
      
      return pricing; // Return current pricing (could be empty if no cache)
    }
  };

  // Initialize pricing on startup
  useEffect(() => {
    const initializePricing = async () => {
      setIsLoading(true);
      
      // First try to load from cache
      const hasCachedData = loadCachedPricing();
      
      if (!hasCachedData) {
        // If no valid cache, fetch from API
        await fetchPricing(false); // Don't show toast on startup
      }
      
      setIsLoading(false);
    };

    initializePricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manual refresh function
  const refreshPricing = async () => {
    setIsLoading(true);
    await fetchPricing(true); // Show toast on manual refresh
    setIsLoading(false);
  };

  // Clear cache function
  const clearCache = () => {
    localStorage.removeItem(CACHE_KEY);
    setLastFetchTime(null);
    console.log('🗑️ Pricing cache cleared');
  };

  const value = {
    pricing,
    isLoading,
    error,
    lastFetchTime,
    refreshPricing,
    clearCache,
    isCached: !!lastFetchTime
  };

  return (
    <PricingContext.Provider value={value}>
      {children}
    </PricingContext.Provider>
  );
};

export const usePricing = () => {
  const context = useContext(PricingContext);
  if (!context) {
    throw new Error('usePricing must be used within a PricingProvider');
  }
  return context;
};